"use client";

import { useContext, useEffect, useRef, useState } from "react";
import {
  AbsoluteFill,
  getRemotionEnvironment,
  Internals,
  useBufferState,
} from "remotion";

import { catHasAppeared, PurringCatLoader } from "../PurringCatLoader";

/** A later stall shows the cat only after this much buffering (the first wait shows her at once). */
export const LOADING_CAT_DELAY_MS = 300;
/** Each half of the dip to white at the end of the first wait. */
export const CAT_DIP_MS = 200;
/** A later stall's cat fades out this fast (no dip, no hold). */
export const CAT_STALL_FADE_MS = 150;

type Phase =
  | "hidden"
  | "showing"
  | "to-white" // first wait over: the cat fades to white, card held at frame 0
  | "from-white" // the card plays, the white dissolves over it
  | "fading"; // a later stall is over: the cat fades out quickly

const DIP_CSS = `
@keyframes wgc-dip-to-white { from { opacity: 0; } to { opacity: 1; } }
@keyframes wgc-dip-from-white { from { opacity: 1; } to { opacity: 0; } }
`;

/**
 * Draws the purring cat over the card while the Player is buffering (#791).
 *
 * Why not Remotion's own `showPosterWhenBuffering`: in 4.0.150 PlayerUI's
 * "resume" handler sets a `stopped` flag that is never reset, and the buffer
 * manager announces "resume" as soon as the Player starts listening (nothing is
 * held yet), so the buffering poster can never show afterwards.
 *
 * So this listens to the same buffer manager from inside the composition. It
 * sees every hold: HoldPlaybackUntilAssetsLoaded, and any <Video>/<Audio>/<Img>
 * still loading. The manager re-announces its current state when a listener
 * registers, so there is no mount-order race.
 *
 * The card's first wait shows her at once, continuing the page-level cat that
 * covered the time before the Player mounted (see View/Preview). When it ends
 * she DIPS TO WHITE (Laura, 2026-10-18, a film idiom): she fades to white while
 * the card is held at frame 0, then the card starts and the white dissolves
 * over it, so the opening isn't missed. Only if she was actually on screen; a
 * fast load that never showed her just gets out of the way. Later stalls wait
 * 300ms before she appears, and she fades out quickly when they end.
 *
 * Never drawn while rendering: the media hold the render too (delayRender), and
 * a cat baked into the Lambda MP4 would ship to recipients.
 */
export const BufferingCatOverlay = () => {
  const manager = useContext(Internals.BufferingContextReact);
  const buffer = useBufferState();
  // Read through a ref: the listeners below are set up once per buffer manager,
  // and must not be torn down (releasing the dip hold) when a render happens to
  // produce a new buffer-state object.
  const bufferRef = useRef(buffer);
  bufferRef.current = buffer;
  // Visible from mount: the page showed a cat until the Player mounted, and the
  // card's first wait must follow on with no gap. The manager re-announces its
  // state when we start listening, so a card with nothing to wait for hides her
  // within a frame.
  const [phase, setPhase] = useState<Phase>("showing");
  const phaseRef = useRef<Phase>("showing");
  const firstWait = useRef(true);
  // The first wait keeps the loader's CSS fade-in delay (in step with the page
  // cat); later stalls have already waited 300ms in code, so they skip it.
  const [laterStall, setLaterStall] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const stallTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dipHold = useRef<(() => void) | null>(null);
  // The manager's latest word, tracked even while dipping (when its
  // announcements are otherwise ignored). Once the dip's own hold is released,
  // "still blocked" can only mean media — see the end of the dip.
  const blocked = useRef(false);

  useEffect(() => {
    if (!manager) {
      setPhase("hidden");
      return;
    }
    const go = (next: Phase) => {
      phaseRef.current = next;
      setPhase(next);
    };
    const later = (ms: number, fn: () => void) => {
      timers.current.push(setTimeout(fn, ms));
    };
    const releaseDipHold = () => {
      dipHold.current?.();
      dipHold.current = null;
    };
    const dipping = () =>
      phaseRef.current === "to-white" || phaseRef.current === "from-white";

    // Later stalls: only after 300ms, and a stream of announcements must not
    // keep pushing that back.
    const scheduleStall = () => {
      if (phaseRef.current === "showing" || stallTimer.current) return;
      stallTimer.current = setTimeout(() => {
        stallTimer.current = null;
        // Later-stall mode only when a later stall actually shows: it starts
        // her fresh and drops the CSS fade-in (this one already waited 300ms).
        setLaterStall(true);
        go("showing");
      }, LOADING_CAT_DELAY_MS);
    };

    const onBuffering = manager.listenForBuffering(() => {
      blocked.current = true;
      // Our own dip hold announces "buffering" too; it isn't a stall. A REAL
      // stall that starts mid-dip is picked up when the dip ends (blocked).
      if (dipping()) return;
      if (firstWait.current) return go("showing");
      scheduleStall();
    });

    const onResume = manager.listenForResume(() => {
      blocked.current = false;
      if (stallTimer.current) clearTimeout(stallTimer.current);
      stallTimer.current = null;
      // Releasing our own dip hold announces "resume"; the dip carries on.
      if (dipping()) return;

      if (firstWait.current) {
        firstWait.current = false;
        // Not later-stall mode yet: switching now would drop the first wait's
        // CSS fade-in mid-fade and flash her to full opacity during the dip
        // (Copilot on #791).
        if (phaseRef.current === "showing" && catHasAppeared()) {
          // Dip to white: hold the card at frame 0 while she fades to white...
          dipHold.current = bufferRef.current.delayPlayback().unblock;
          go("to-white");
          later(CAT_DIP_MS, () => {
            // ...then let the card play and dissolve the white over it.
            go("from-white");
            releaseDipHold();
            later(CAT_DIP_MS, () => {
              go("hidden");
              // Our hold is long gone, so anything still blocking is media that
              // stalled during the dissolve (Copilot on #791): treat it as a
              // later stall rather than leaving a frozen card with no cat.
              if (blocked.current) scheduleStall();
            });
          });
          return;
        }
        return go("hidden");
      }
      if (phaseRef.current === "showing") {
        go("fading");
        later(CAT_STALL_FADE_MS, () => go("hidden"));
      }
    });

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
      if (stallTimer.current) clearTimeout(stallTimer.current);
      releaseDipHold();
      onBuffering.remove();
      onResume.remove();
    };
  }, [manager]);

  if (phase === "hidden" || getRemotionEnvironment().isRendering) return null;

  const dip = phase === "to-white" || phase === "from-white";
  return (
    <AbsoluteFill style={{ zIndex: 1000, pointerEvents: "none" }}>
      <style>{DIP_CSS}</style>
      {phase !== "from-white" ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            animation:
              phase === "fading"
                ? `wgc-dip-from-white ${CAT_STALL_FADE_MS}ms ease-out forwards`
                : undefined,
          }}
        >
          {/* The first wait continues the page cat's walk; a later stall has
              nothing to continue, so she starts fresh (and skips the CSS delay). */}
          <PurringCatLoader
            handover={laterStall ? undefined : "resume"}
            appear={!laterStall}
          />
        </div>
      ) : null}
      {dip ? (
        <div
          data-testid="cat-dip"
          data-phase={phase}
          style={{
            position: "absolute",
            inset: 0,
            background: "#ffffff",
            animation: `${phase === "to-white" ? "wgc-dip-to-white" : "wgc-dip-from-white"} ${CAT_DIP_MS}ms ease-in-out forwards`,
          }}
        />
      ) : null}
    </AbsoluteFill>
  );
};
